import { lazy, type ReactNode, Suspense, useCallback, useEffect, useRef } from 'react';
import { createMemoryRouter, Navigate, Outlet, RouterProvider, useLocation, useNavigate, useParams, useRouteError } from 'react-router-dom';
import { trackScreen } from './analytics/analytics';
import { tabScreen } from './analytics/events';
import { track } from './analytics/track';
import { useConfig } from './api/queries';
import type { TabBarItem } from './api/types';
import { routeFromStartParam } from './deeplink';
import { gameOff, gameRouteRedirect } from './gameSwitch';
import { favoritesCount, useFavoritesState } from './session/favorites';
import { sendReferral } from './session/referral';
import { lang, t } from './i18n/i18n';
import { FavoritesScreen } from './screens/FavoritesScreen';
import { HomeScreen } from './screens/HomeScreen';
import { LeaguesScreen } from './screens/LeaguesScreen';
import { NewsHubScreen } from './screens/news/NewsHubScreen';
import { isArticlePath } from './screens/news/newsTabs';
import { MatchesScreen } from './screens/MatchesScreen';
import { Placeholder } from './screens/Placeholder';
import { useExitWorld } from './screens/world/useExitWorld';
import { SplashView } from './screens/world/SplashView';
import { isWorldPath, worldPathOf } from './screens/world/worldModel';
import { hasWorldDetail } from './screens/world/worldDetailParams';
import { gateDecision, launchAllowsOnboarding, ONBOARDING_ROUTE, onboardingMark, priorOnboardingMark, setOnboardingMark } from './screens/onboarding/onboardingModel';
import { autoTourOff, configHasTour, launchAllowsTour, tourHolds, useTourState, wantTour } from './tour/launch';
import { bindBackButton, isMock, startParam } from './telegram/webapp';
import { ErrorView, Loading, preloadOfflineArt, screenError } from './ui/States';
import { AppBar } from './ui/AppBar';
import { KeyedRoute } from './ui/KeyedRoute';
import { TabBar } from './ui/TabBar';
import { toast, ToastHost } from './ui/Toast';
import { OfflineBanner } from './ui/OfflineBanner';
import { PullToRefresh } from './ui/PullToRefresh';
import { tabBarOf } from './ui/fallbackTabs';
import styles from './App.module.css';

/** Экраны вкладок по серверному ключу. Ключ без экрана в панель не попадает (незнакомое — молча пропускаем). */
export const TAB_KEYS = ['matches', 'leagues', 'home', 'news', 'highlights', 'favorites', 'game', 'fantasy'];
const GAME_TAB_KEYS = ['game', 'fantasy'];
function TabScreen({ item }: { item: TabBarItem }) {
  switch (item.key) {
    case 'matches': return <MatchesScreen path={item.url} />;
    case 'leagues': return <LeaguesScreen path={item.url} />;
    case 'home': return <HomeScreen path={item.url} />;
    case 'news': return <NewsHubScreen path={item.url} />;
    case 'highlights': return <HighlightsTab path={item.url} />;
    case 'favorites': return <FavoritesScreen path={item.url} />;
    case 'game': return <GameScreen path={item.url} />;
    // §288: вход в игровой мир вкладкой Mini App; url — адрес мира (без мира — прежний экран игры)
    case 'fantasy': return isWorldPath(item.url) ? <Navigate to="/world" replace /> : <GameScreen path={item.url} />;
    default: return <Placeholder title={item.label} />;
  }
}

function useTabs() {
  const config = useConfig();
  // конфига нет без сети — встроенный набор (HQ-1112): шапка и панель остаются, «нет сети» — у экрана вкладки
  const tabBar = tabBarOf(config);
  // игра выключена (HQ-1572) — вкладок игры нет, даже если панель их прислала
  const off = gameOff(config.data);
  const items = (tabBar?.items ?? []).filter((i) => TAB_KEYS.includes(i.key) && !(off && GAME_TAB_KEYS.includes(i.key)));
  const defaultKey = items.some((i) => i.key === tabBar?.default_key) ? tabBar!.default_key : items[0]?.key;
  return { config, items, defaultKey };
}

function Shell() {
  const { config, items } = useTabs();
  const location = useLocation();
  const navigate = useNavigate();
  const isTabRoot = location.pathname.startsWith('/tab/') || location.pathname === '/';
  // Игровой мир (§2.18) — со своей нижней панелью: панель и шапка приложения на это время скрыты.
  const inWorld = location.pathname === '/world' || location.pathname.startsWith('/world/');
  // экран внутри раздела мира (/world/player/cards): «Назад» — к разделу, а не выход из мира
  // HQ-2344: открыт слой страницы клуба или игроков страны (?club= / ?fans=) — «Назад» снимает слой
  const inWorldSub = /^\/world\/[^/]+\/[^/]+/.test(location.pathname) || (inWorld && hasWorldDetail(location.search));
  // статья новости (Figma 772:32587) и онбординг (HQ-1092) — без нижней панели, как мир
  const inOnboarding = location.pathname === ONBOARDING_ROUTE;
  const hideTabBar = isArticlePath(location.pathname) || inOnboarding;
  useEffect(() => {
    if (!hideTabBar) return;
    document.documentElement.dataset.noTabbar = '';
    return () => { delete document.documentElement.dataset.noTabbar; };
  }, [hideTabBar]);
  const exitWorld = useExitWorld();
  // На вложенном экране (матч, команда…) подсвечена вкладка, из которой пришли.
  const lastTab = useRef('');
  if (isTabRoot) lastTab.current = location.pathname.split('/')[2] ?? '';
  const activeKey = lastTab.current;

  // В мире системная «Назад» — выход к крикету (как кнопка exit_title), а не шаг по истории разделов.
  const toWorldExit = inWorld && !inWorldSub;
  // приглашение друга: код из ссылки запуска — на сервер, как только известен адрес мира (вход — лениво, сам)
  const worldPath = worldPathOf(config.data);
  useEffect(() => {
    if (!worldPath) return;
    void sendReferral(worldPath).then((attached) => { if (attached) toast(t('tg_friends_attached')); });
  }, [worldPath]);
  const back = useCallback(() => (toWorldExit ? exitWorld() : navigate(-1)), [toWorldExit, exitWorld, navigate]);
  // онбординг — корень запуска: «Назад» по шагам ведёт сам экран (окно поверх), со старта уходить некуда
  const showBack = !isTabRoot && !inOnboarding;
  useEffect(() => bindBackButton(showBack, back), [showBack, back]);
  // язык на <html> (data-lang): капслок и трекинг в css — только под [data-lang='en'] (у hi/bn/ta/te — ни того, ни другого)
  useEffect(() => { document.documentElement.dataset.lang = lang; }, []);

  // тур по приложению (HQ-2338): кусок тура — только когда он может начаться (см. tour/launch.ts)
  const tour = useTourState();
  const autoTour = launchAllowsTour({ launchParam, firstPath });
  const hasTour = configHasTour(config.data);
  // свежий ответ config этого запуска (не кэш прошлого); не пришёл (нет сети) или тура в нём нет — сам тур не начнётся
  const configFresh = config.dataUpdatedAt > 0 && !config.isPlaceholderData;
  const configSettled = !config.isFetching;
  useEffect(() => { if (!autoTour || (configSettled && !(configFresh && hasTour))) autoTourOff(); }, [autoTour, configSettled, configFresh, hasTour]);
  const showTour = wantTour({ pathname: location.pathname, hasTour, configFresh, state: tour, autoAllowed: autoTour });
  // кусок тура — заранее, как только ясно, что тур в config есть: старт не ждёт скачивания (≤ 1 с после первой страницы)
  useEffect(() => { if (hasTour && (autoTour || tour.menu)) loadTour().catch(() => {}); }, [hasTour, autoTour, tour.menu]);

  // вход сразу в мир (ссылка, перезапуск на экране мира) — заставка мира, а не шиммер приложения; без сети первый
  // запрос конфига не ждём — оболочка со встроенными вкладками (tabBarOf)
  if (config.isPending && !items.length) return inWorld ? <SplashView progress={0.03} /> : <Loading query={config} />;
  if (!items.length) return <ErrorView error={config.error} onRetry={() => void config.refetch()} />;
  return (
    <div className={styles.shell}>
      <div className={styles.topScrim} aria-hidden="true" />
      {isMock && showBack && <button className={styles.devBack} onClick={back}>‹ Back (dev)</button>}
      {isTabRoot && <AppBar title={items.find((i) => i.key === activeKey)?.label ?? ''}
        header={config.data?.navigation?.headers?.[activeKey]} tab={activeKey} />}
      <main className={styles.content}><Suspense fallback={<Loading />}><Outlet /></Suspense></main>
      <PullToRefresh />
      <OfflineBanner />
      <ToastHost />
      {/* ярлык на главном экране (HQ-725): лист — только на корне вкладки, не в мире и не поверх обучения */}
      <Suspense fallback={null}><HomeScreenPrompt onTab={isTabRoot && !inWorld && !tourHolds(tour, autoTour)} /></Suspense>
      {showTour && config.data && (
        <Suspense fallback={null}>
          <AppTour key={tour.menu ? 'menu' : 'auto'} config={config.data} menu={tour.menu} onboardingBefore={priorOnboardingMark()} />
        </Suspense>
      )}
      {!inWorld && !hideTabBar && <TabBar items={items} active={activeKey} onSelect={(key) => {
        track('tab_select', { tab: tabScreen(key) });
        navigate(`/tab/${key}`, { replace: isTabRoot });
      }} />}
    </div>
  );
}

function TabRoute() {
  const { key } = useParams();
  const { items, defaultKey } = useTabs();
  const item = items.find((i) => i.key === key);
  if (!item) return defaultKey ? <Navigate to={`/tab/${defaultKey}`} replace /> : null;
  return <TabScreen key={item.key} item={item} />;
}

/** «Игра» вне панели — по кнопке шапки; адрес экрана — endpoint кнопки как есть.
 *  Есть config.game.world_path (§2.18) — вместо экрана открываем игровой мир (и для startapp=game). */
function GameRoute() {
  const query = new URLSearchParams(useLocation().search);
  const config = useConfig().data;
  const fallback = config?.game?.screen_path ?? '/api/v5/mobile/game/cricket/';
  if (worldPathOf(config)) return <Navigate to="/world" replace />;
  return <GameScreen path={query.get('path') ?? fallback} title={query.get('title') ?? undefined} />;
}

/** Маршруты игры и мира (/game…, /world…): игра выключена (HQ-1572) — на «Матчи» (нет такой вкладки — на вкладку по
 *  умолчанию) с заменой истории. Так закрыты все входы: ссылки бота, кнопка шапки, старые закладки. Конфиг ещё
 *  грузится — оболочка сама показывает загрузку (Shell), экран игры до ответа не монтируется. */
function GameGate({ children }: { children: ReactNode }) {
  const { config, items, defaultKey } = useTabs();
  const to = gameRouteRedirect(config.data, items.map((i) => i.key), defaultKey);
  return to ? <Navigate to={to} replace /> : <>{children}</>;
}

function RouteError() {
  const error = useRouteError();
  useEffect(() => { console.error(error); }, [error]);
  // упавший lazy-импорт React запоминает — повтор только перезагрузкой; без сети это «нет сети» (HQ-1112)
  return <ErrorView error={screenError(error)} onRetry={() => window.location.reload()} />;
}

/** Корень: онбординг первого запуска (HQ-1092) или вкладка по умолчанию. Запуск по ссылке и сразу в мир онбординг не
 *  перебивает — он ждёт следующего обычного запуска. Избранное уже есть — вернувшийся человек: отметка, больше не ждём. */
function IndexRoute() {
  const { items, defaultKey } = useTabs();
  const favorites = useFavoritesState();
  const item = items.find((i) => i.key === defaultKey);
  const toWorld = item?.key === 'fantasy' && isWorldPath(item.url);
  const mark = onboardingMark();
  const launch = launchAllowsOnboarding({ startParam: launchParam, toWorld });
  const decision = gateDecision({ launch, mark, session: favorites.session, loaded: favorites.loaded, failed: favorites.failed, paused: favorites.paused, favorites: favorites.set });
  const returning = launch && !mark && favorites.loaded && favoritesCount(favorites.set) > 0;
  useEffect(() => { if (returning) setOnboardingMark('returning'); }, [returning]);
  if (decision === 'wait') return <Loading />;
  // без сети набор на паузе: «Нет сети» с «Retry» (Loading по fetchStatus 'paused'); сеть вернётся — запрос пойдёт сам
  if (decision === 'offline') return <Loading query={favorites.query} />;
  if (decision === 'show') return <Navigate to={ONBOARDING_ROUTE} replace />;
  return defaultKey ? <Navigate to={`/tab/${defaultKey}`} replace /> : null;
}


// лист «На главный экран» (HQ-725) — редкий и не на первом кадре: своим куском после старта (HQ-1993, запас главного
// чанка). Без сети кусок не скачается — листа нет, оболочка цела (ошибку lazy в Shell ловить некому)
const HomeScreenPrompt = lazy(() => import('./homescreen/HomeScreenPrompt')
  .then((m) => ({ default: m.HomeScreenPrompt }), () => ({ default: () => null })));
// тур по приложению (HQ-2338) — своим куском: главный чанк в бюджете; без сети кусок не скачается — тура нет
const loadTour = () => import('./tour/AppTour');
const AppTour = lazy(() => loadTour().then((m) => ({ default: m.AppTour }), () => ({ default: () => null })));
const MatchScreen = lazy(() => import('./screens/match/MatchScreen').then((m) => ({ default: m.MatchScreen })));
const TournamentScreen = lazy(() => import('./screens/tournament/TournamentScreen').then((m) => ({ default: m.TournamentScreen })));
const PlayerScreen = lazy(() => import('./screens/entity/PlayerScreen').then((m) => ({ default: m.PlayerScreen })));
const RankingsScreen = lazy(() => import('./screens/entity/RankingsScreen').then((m) => ({ default: m.RankingsScreen })));
const TeamScreen = lazy(() => import('./screens/entity/TeamScreen').then((m) => ({ default: m.TeamScreen })));
const VenueScreen = lazy(() => import('./screens/entity/VenueScreen').then((m) => ({ default: m.VenueScreen })));
const SearchScreen = lazy(() => import('./screens/SearchScreen').then((m) => ({ default: m.SearchScreen })));
const ArticleScreen = lazy(() => import('./screens/news/ArticleScreen').then((m) => ({ default: m.ArticleScreen })));
const NewsFeedScreen = lazy(() => import('./screens/news/NewsFeedScreen').then((m) => ({ default: m.NewsFeedScreen })));
const HighlightsTab = lazy(() => import('./screens/news/VideosScreen').then((m) => ({ default: ({ path }: { path: string }) => <m.VideosContent league={null} path={path} /> })));
const VideosScreen = lazy(() => import('./screens/news/VideosScreen').then((m) => ({ default: m.VideosScreen })));
const CompareScreen = lazy(() => import('./screens/entity/CompareScreen').then((m) => ({ default: m.CompareScreen })));
const CompareTeamsScreen = lazy(() => import('./screens/entity/CompareTeamsScreen').then((m) => ({ default: m.CompareTeamsScreen })));
const RecordsScreen = lazy(() => import('./screens/entity/RecordsScreen').then((m) => ({ default: m.RecordsScreen })));
const GameScreen = lazy(() => import('./screens/game/GameScreen').then((m) => ({ default: m.GameScreen })));
const GameFixturesScreen = lazy(() => import('./screens/game/GameScreen').then((m) => ({ default: m.GameFixturesScreen })));
const LeagueScreen = lazy(() => import('./screens/game/LeagueScreen').then((m) => ({ default: m.LeagueScreen })));
const LeagueJoinScreen = lazy(() => import('./screens/game/LeagueScreen').then((m) => ({ default: m.LeagueJoinScreen })));
const LeaderboardScreen = lazy(() => import('./screens/game/LeaderboardScreen').then((m) => ({ default: m.LeaderboardScreen })));
const loadWorld = () => import('./screens/world/WorldScreen');
const LazyWorld = lazy(() => loadWorld().then((m) => ({ default: m.WorldScreen })));
/** Пока скачивается экран мира — та же заставка, что и у загрузки мира (не шиммер в три блока). */
function WorldScreen() {
  return <Suspense fallback={<SplashView progress={0.05} />}><LazyWorld /></Suspense>;
}
// экран мира — заранее, когда приложение простаивает: вход в мир без ожидания куска
if (typeof window !== 'undefined') {
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 2000));
  // без сети кусок не скачается — молча: открытие мира покажет «нет сети» само (RouteError)
  idle(() => { loadWorld().catch(() => {}); });
}
// выбор любимых по шагам сервера (HQ-726) — с карточки «Выберите команды» на главной
const QuizScreen = lazy(() => import('./screens/quiz/QuizScreen').then((m) => ({ default: m.QuizScreen })));
const PickTeamsScreen = lazy(() => import('./screens/pickTeams/PickTeamsScreen').then((m) => ({ default: m.PickTeamsScreen })));
const SettingsScreen = lazy(() => import('./screens/settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen })));
const LanguageScreen = lazy(() => import('./screens/settings/LanguageScreen').then((m) => ({ default: m.LanguageScreen })));
const ContactsScreen = lazy(() => import('./screens/settings/ContactsScreen').then((m) => ({ default: m.ContactsScreen })));
const PushSettingsScreen = lazy(() => import('./screens/settings/PushSettingsScreen').then((m) => ({ default: m.PushSettingsScreen })));
// пейвол подписки «без рекламы» за Telegram Stars (HQ-1091); offer: null или активная подписка — уходит на главную
// онбординг «Быстрая настройка» первого запуска (HQ-1092): старт → шаги выбора → уведомления → пейвол
const OnboardingScreen = lazy(() => import('./screens/onboarding/OnboardingScreen').then((m) => ({ default: m.OnboardingScreen })));
const PaywallScreen = lazy(() => import('./screens/premium/PaywallScreen').then((m) => ({ default: m.PaywallScreen })));
// проверка рекламы Monetag в настоящем Telegram (startapp=dev_ads) — только dev-сборка, в прод-бандл не попадает
const AdsDebug = import.meta.env.DEV ? lazy(() => import('./ads/AdsDebug')) : null;
// dev: ?route=/settings — открыть любой экран сразу (обход вёрстки скриптами); в сборку прода не попадает
const devRoute = import.meta.env.DEV ? new URLSearchParams(window.location.search).get('route') : null;
const deepLink = devRoute ?? routeFromStartParam(startParam());
/** Параметр запуска (startapp или dev ?route=): любой — запуск по ссылке, онбординг его не перебивает. */
const launchParam = devRoute ?? startParam() ?? null;
// отметка онбординга с прошлых запусков — сейчас, до записи этого запуска (тур отличает по ней нового человека)
priorOnboardingMark();
/** Первый экран запуска после корня: сразу в мир — тур сам не начинается. */
let firstPath: string | null = deepLink;
const router = createMemoryRouter([
  {
    element: <Shell />,
    // Упал экран — наш экран ошибки на месте контента (вкладки живы), а не трассировка react-router.
    children: [{ errorElement: <RouteError />, children: [
      { path: '/', element: <IndexRoute /> },
      { path: '/tab/:key', element: <TabRoute /> },
      { path: '/match/:id', element: <KeyedRoute><MatchScreen /></KeyedRoute> },
      { path: '/game', element: <GameGate><GameRoute /></GameGate> },
      { path: '/world', element: <GameGate><WorldScreen /></GameGate> },
      { path: '/world/:tab', element: <GameGate><WorldScreen /></GameGate> },
      { path: '/world/:tab/:sub', element: <GameGate><WorldScreen /></GameGate> },
      // мини-игра «Отбей подачу» — тренировка; вход с карты калиток, когда сервер её пришлёт (пока — только по адресу)
      { path: '/game/leaderboard', element: <GameGate><LeaderboardScreen /></GameGate> },
      { path: '/game/fixtures', element: <GameGate><GameFixturesScreen /></GameGate> },
      { path: '/game/league/:id', element: <GameGate><LeagueScreen /></GameGate> },
      { path: '/game/join/:code', element: <GameGate><LeagueJoinScreen /></GameGate> },
      { path: '/team/:id', element: <KeyedRoute><TeamScreen /></KeyedRoute> },
      { path: '/player/:id', element: <PlayerScreen /> },
      { path: '/league/:id', element: <TournamentScreen /> },
      { path: '/search', element: <SearchScreen /> },
      { path: '/compare', element: <CompareScreen /> },
      { path: '/compare-teams', element: <CompareTeamsScreen /> },
      { path: '/onboarding/favorites', element: <PickTeamsScreen /> },
      { path: '/onboarding/setup', element: <OnboardingScreen /> },
      { path: '/settings', element: <SettingsScreen /> },
      { path: '/settings/push', element: <PushSettingsScreen /> },
      { path: '/premium', element: <PaywallScreen /> },
      { path: '/language', element: <LanguageScreen /> },
      { path: '/contacts', element: <ContactsScreen /> },
      { path: '/records', element: <RecordsScreen /> },
      { path: '/videos', element: <VideosScreen /> },
      { path: '/news/:id', element: <KeyedRoute><ArticleScreen /></KeyedRoute> },
      { path: '/feed', element: <NewsFeedScreen /> },
      { path: '/venue/:id', element: <VenueScreen /> },
      { path: '/rankings', element: <RankingsScreen /> },
      { path: '/quiz', element: <QuizScreen /> },
      ...(AdsDebug ? [{ path: '/dev/ads', element: <AdsDebug /> }] : []),
      { path: '*', element: <IndexRoute /> },
    ] }],
  },
], { initialEntries: deepLink ? ['/', deepLink] : ['/'], initialIndex: deepLink ? 1 : 0 });

// экраны — в аналитику: первый и каждая смена маршрута. Аналитика стартует позже (main.tsx) — событие ждёт в очереди
// track.ts и уходит сразу после tg_launch. Первый экран холодного старта по ссылке — источник link.
trackScreen(router.state.location, deepLink ? 'link' : undefined);
router.subscribe((state) => {
  trackScreen(state.location);
  if (firstPath === null && state.location.pathname !== '/') firstPath = state.location.pathname;
});

export function App() {
  useEffect(preloadOfflineArt, []);
  return <RouterProvider router={router} />;
}
